import React from "react";
import _ from "lodash";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import { CircularProgress, Typography } from "@material-ui/core";

import { Dropzone } from "../inputs/Dropzone";
import { Button } from "../inputs/Button";
import { TextField } from "../inputs/TextField";
import { CAM } from "../../constants/interfaces";
import { camActions } from "../../reducers/cam";
import {
  GENERAL_FILE_TYPES,
  MAX_FILE_SIZE,
  GENERAL_FILE_HINT
} from "../../constants/files";
import { ErrorMessage } from "../Messages";
import { ConfirmContext } from "../../components/ConfirmContext";
import { UIMessageContext } from "../../components/UIMessage";
import api from "../../utils/api";
import { INFORMATION_ACCURACY_CONFIRMATION } from "../../constants/messages";
import { OnBeforeUnload } from "../../components/navigation/OnBeforeUnload";

type Props = {
  history: any;
  cam: CAM;
  classes: any;
  loading: boolean;
  error?: Error;
  updateCam: (cam: CAM, postUpdateCallBack: () => void) => Promise<void>;
};

type State = {
  attachments: File[];
  response: string;
};

const styles = (theme: Theme) =>
  createStyles({
    buttonRow: {
      display: "flex",
      justifyContent: "flex-end",
      width: "100%"
    },
    dropzoneWrapper: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    dropzone: {
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(1),
      maxWidth: "600px"
    },
    loadingWrapper: {
      padding: `0 ${theme.spacing(2)}px`,
      marginRight: theme.spacing(2)
    },
    message: {
      marginLeft: "auto",
      marginRight: "auto",
      maxWidth: theme.breakpoints.values.md
    },
    messageWrapper: {
      margin: theme.spacing(3),
      width: "100%"
    },
    responseField: {
      width: "100%",
      minWidth: "200px",
      margin: theme.spacing(2)
    },
    root: {
      display: "flex",
      flexFlow: "row wrap"
    },
    submitButton: {
      marginRight: theme.spacing(2)
    }
  });

class ResponseTab extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      attachments: [],
      response: ""
    };
    this.onCancel = this.onCancel.bind(this);
    this.onUpdateResponse = this.onUpdateResponse.bind(this);
  }

  onCancel() {
    this.props.history.goBack();
  }

  onUpdateResponse(e: any) {
    e.persist();
    this.setState({
      response: e.target.value
    });
  }

  render() {
    const { classes, error, loading, cam } = this.props;
    const { attachments, response } = this.state;
    return (
      <UIMessageContext.Consumer>
        {({ showMessage }) => {
          return (
            <div className={classes.root}>
              {(response || attachments.length > 0) && <OnBeforeUnload />}

              <TextField
                id="response"
                characterCounter={true}
                className={classes.responseField}
                InputLabelProps={{ shrink: true }}
                label="Response"
                onChange={this.onUpdateResponse}
                placeholder="Your corrective action response"
                required
                multiline
                rows={3}
                inputProps={{
                  maxLength: 1300
                }}
                value={response}
              />
              <div className={classes.dropzoneWrapper}>
                <Typography color="textSecondary" variant="caption">
                  Supporting documents
                </Typography>
                <Dropzone
                  acceptedFileTypes={GENERAL_FILE_TYPES}
                  className={classes.dropzone}
                  files={attachments}
                  hint={GENERAL_FILE_HINT}
                  id="attachments"
                  maxFileCount={10}
                  maxTotalByteSize={MAX_FILE_SIZE}
                  onNewFiles={(files: File[]) => {
                    try {
                      Promise.all(
                        files.map(file => {
                          const formData: any = new FormData();
                          formData.append("uploadfile", file);
                          return api.post(
                            `/cam/uploadFile/${cam.caseId}`,
                            formData
                          );
                        })
                      ).then(() => {
                        this.setState({
                          attachments: [...this.state.attachments, ...files]
                        });
                      });
                    } catch (err) {
                      showMessage(
                        _.get(err, "response.data.message") || err.message,
                        "error"
                      );
                    }
                  }}
                  onRemoveFile={(file: File, index: number) => {
                    const { cam } = this.props;
                    try {
                      api
                        .put(`/cam/removeFile/${cam.caseId}`, file.name, {
                          headers: {
                            "Content-Type": "application/json"
                          }
                        })
                        .then(() => {
                          const attachments = [...this.state.attachments];
                          attachments.splice(index, 1);
                          this.setState({
                            attachments
                          });
                        });
                    } catch (err) {
                      showMessage(
                        _.get(err, "response.data.message") || err.message,
                        "error"
                      );
                    }
                  }}
                />
              </div>
              <div className={classes.messageWrapper}>
                {error && (
                  <ErrorMessage className={classes.message} error={error} />
                )}
              </div>
              <div className={classes.buttonRow}>
                {loading ? (
                  <div className={classes.loadingWrapper}>
                    <CircularProgress />
                  </div>
                ) : (
                  <Button
                    id="submit"
                    disabled={response.trim().length === 0}
                    className={classes.submitButton}
                    color="primary"
                    onClick={async () => {
                      const { response } = this.state;
                      const { cam, updateCam, history } = this.props;

                      await this.context.confirm(
                        INFORMATION_ACCURACY_CONFIRMATION
                      );

                      updateCam(
                        { ...cam, correctiveActionResponse: response },
                        () => {
                          this.setState({response: "", attachments: []})
                          history.goBack();
                          showMessage(
                            "Your response was successfully submitted",
                            "success"
                          );
                        }
                      );
                    }}
                    type="submit"
                  >
                    Submit
                  </Button>
                )}

                <Button id="cancel" onClick={this.onCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          );
        }}
      </UIMessageContext.Consumer>
    );
  }
}

ResponseTab.contextType = ConfirmContext;

const mapDispatchToProps = {
  updateCam: camActions.updateCam
};

const mapStateToProps = ({ cam }: any) => ({
  error: cam.error,
  loading: cam.loading
});

export const styled = withStyles(styles)(ResponseTab);
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled);
